import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/** Utilisation state of the meter, derived from `value / max`. */
export type ZagMeterState = 'ok' | 'warn' | 'over';

/**
 * Compact horizontal utilisation meter (`role="meter"`), e.g. booked hours
 * against a budget. The fill is clamped at 100%; utilisation ≥ `warnAt`
 * renders as warning, above 100% or ≥ `dangerAt` as error. Purely visual — the parent
 * renders any numeric label next to it.
 */
@Component({
  selector: 'zag-meter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-meter',
    '[class.zag-meter--warn]': 'state() === "warn"',
    '[class.zag-meter--over]': 'state() === "over"',
    role: 'meter',
    '[attr.aria-valuemin]': '0',
    '[attr.aria-valuemax]': 'max()',
    '[attr.aria-valuenow]': 'value()',
    '[attr.aria-label]': 'ariaLabel()',
  },
  template: `<span class="zag-meter__fill" [style.width.%]="fillPercent()"></span>`,
  styles: `
    :host {
      display: flex;
      overflow: hidden;
      width: 100%;
      height: var(--spacing-xs);
      border-radius: var(--border-radius-full);
      background: var(--background-color-tertiary);
    }

    .zag-meter__fill {
      border-radius: var(--border-radius-full);
      background: var(--color-success);
      transition: width var(--transition-duration-fast, 150ms);
    }

    :host(.zag-meter--warn) .zag-meter__fill {
      background: var(--color-warning);
    }

    :host(.zag-meter--over) .zag-meter__fill {
      background: var(--color-error);
    }

    @media (prefers-reduced-motion: reduce) {
      .zag-meter__fill {
        transition: none;
      }
    }
  `,
})
export class ZagMeter {
  /** Current value (e.g. booked hours). Negative values are clamped to 0. */
  readonly value = input.required<number>();
  /** Capacity the value is measured against (e.g. budget hours). */
  readonly max = input.required<number>();
  /** Accessible name of the meter (there is no visible label). */
  readonly ariaLabel = input.required<string>();
  /** Utilisation ratio from which the meter renders as warning. */
  readonly warnAt = input<number>(0.9);
  /** Utilisation ratio from which the meter renders as error; unset never triggers before 100%. */
  readonly dangerAt = input<number>(Number.POSITIVE_INFINITY);

  protected readonly ratio = computed(() => {
    const max = this.max();
    if (max <= 0) return this.value() > 0 ? Number.POSITIVE_INFINITY : 0;
    return Math.max(0, this.value()) / max;
  });

  readonly state = computed<ZagMeterState>(() => {
    const ratio = this.ratio();
    if (ratio > 1 || ratio >= this.dangerAt()) return 'over';
    if (ratio >= this.warnAt()) return 'warn';
    return 'ok';
  });

  protected readonly fillPercent = computed(() => Math.min(100, this.ratio() * 100));
}
